import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { getRoomBlocks } from 'services/nh-room-dashboard';
import { useDashboardTheme, cardSx, panelHeaderSx, countBadgeSx, tableHeadRowSx, tableRowSx, scrollbarSx, fmt, fmtBtc, fmtTimeMedium, truncHash } from '../utils';

// ── Coin badge color map ────────────────────────────────────────────────────
const COIN_COLORS = {
  BTC:  { bg: '#f7931a', text: '#fff' },
  KAS:  { bg: '#49eacb', text: '#0a2e26' },
  RVN:  { bg: '#384182', text: '#fff' },
  LTC:  { bg: '#345d9d', text: '#fff' },
  DOGE: { bg: '#c2a633', text: '#fff' },
  BCH:  { bg: '#0ac18e', text: '#fff' },
  ALPH: { bg: '#5c4fe5', text: '#fff' },
};
const DEFAULT_COIN_COLOR = { bg: '#6b7280', text: '#fff' };
const DEFAULT_HIDDEN_COINS = new Set(['KAS']);

const coinColor = (coin) => COIN_COLORS[coin?.toUpperCase()] || DEFAULT_COIN_COLOR;

// ── Position badge helpers ──────────────────────────────────────────────────
const POSITION_COLORS = ['#fbbf24', '#a8b4c4', '#cd7f32']; // gold, silver, bronze

function PositionBadge({ position }) {
  const bg = POSITION_COLORS[position - 1] || 'rgba(255,255,255,0.08)';
  return (
    <MDBox
      component='span'
      sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: '6px', mr: 1, flexShrink: 0,
        background: bg, fontWeight: 800, fontSize: '0.68rem', lineHeight: 1,
        color: position <= 3 ? '#1a1a2e' : '#fff',
      }}
    >
      {position}
    </MDBox>
  );
}

// ── Winner row inside expanded block ────────────────────────────────────────
function WinnerRow({ w, t }) {
  return (
    <MDBox
      display='flex' alignItems='center' gap={1}
      sx={{
        px: 1.5, py: 0.75, borderRadius: '8px', mb: 0.5,
        background: t.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
        '&:last-child': { mb: 0 },
      }}
    >
      <PositionBadge position={w.position} />
      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.76rem', color: t.primaryText, minWidth: 80 }}>
        {w.name}
      </MDTypography>
      <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText, ml: 'auto', fontFamily: 'monospace' }}>
        {fmtBtc(w.winBtc)} BTC
      </MDTypography>
      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.73rem', color: t.primaryText }}>
        ${fmt(w.winUsd)}
      </MDTypography>
    </MDBox>
  );
}

// ── Boss row inside expanded block ──────────────────────────────────────────
function BossRow({ bossWin, t }) {
  return (
    <MDBox
      display='flex' alignItems='center' gap={1}
      sx={{
        px: 1.5, py: 0.75, borderRadius: '8px', mt: 0.5,
        background: t.isDark ? 'rgba(251,191,36,0.10)' : 'rgba(251,191,36,0.08)',
        border: '1px solid rgba(251,191,36,0.25)',
      }}
    >
      <MDBox
        component='span'
        sx={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          px: 0.8, height: 22, borderRadius: '6px', mr: 0.5, flexShrink: 0,
          background: '#fbbf24', fontWeight: 800, fontSize: '0.62rem', lineHeight: 1,
          color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: '0.04em',
        }}
      >
        Boss
      </MDBox>
      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.76rem', color: '#fbbf24', minWidth: 80 }}>
        {bossWin.name}
      </MDTypography>
      <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText, ml: 'auto', fontFamily: 'monospace' }}>
        {fmtBtc(bossWin.winBtc)} BTC
      </MDTypography>
      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.73rem', color: '#fbbf24' }}>
        ${fmt(bossWin.winUsd)}
      </MDTypography>
    </MDBox>
  );
}

// ── Single block row ────────────────────────────────────────────────────────
function BlockRow({ block, index, t }) {
  const [open, setOpen] = useState(false);
  const winners = block.winners || [];
  const bossWin = block.bossWin;
  const hasDetail = winners.length > 0 || bossWin;

  return (
    <>
      <TableRow sx={{ ...tableRowSx(t, { index }), cursor: hasDetail ? 'pointer' : 'default' }} onClick={hasDetail ? () => setOpen(!open) : undefined}>
        <TableCell>
          <MDBox display='flex' alignItems='center'>
            {hasDetail && (
              <IconButton size='small' onClick={(e) => { e.stopPropagation(); setOpen(!open); }} sx={{ color: t.tableHeaderColor, p: 0, mr: 0.5 }}>
                <Icon fontSize='small'>{open ? 'expand_less' : 'expand_more'}</Icon>
              </IconButton>
            )}
            <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText }}>{fmtTimeMedium(block.createdAt)}</MDTypography>
          </MDBox>
        </TableCell>
        <TableCell>
          <CoinBadge coin={block.coin} />
        </TableCell>
        <TableCell align='right'>
          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText, fontFamily: 'monospace' }}>{fmtBtc(block.payoutRewardBtc)}</MDTypography>
        </TableCell>
        <TableCell align='right'>
          <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(block.payoutRewardUsd)}</MDTypography>
        </TableCell>
        <TableCell>
          <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText, fontFamily: 'monospace' }}>{truncHash(block.blockHash)}</MDTypography>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0, px: 0, borderBottom: `1px solid ${t.rowBorder}` }}>
            <Collapse in={open}>
              <MDBox sx={{ px: { xs: 2, sm: 3 }, py: 1.25 }}>
                {winners.map((w, i) => (
                  <WinnerRow key={i} w={w} t={t} />
                ))}
                {bossWin && <BossRow bossWin={bossWin} t={t} />}
              </MDBox>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ── Inline coin badge (inside table rows) ───────────────────────────────────
function CoinBadge({ coin }) {
  const c = coinColor(coin);
  return (
    <MDBox
      component='span'
      sx={{
        display: 'inline-block', px: 0.9, py: 0.15, borderRadius: '5px',
        fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.5, letterSpacing: '0.03em',
        background: c.bg, color: c.text,
      }}
    >
      {coin}
    </MDBox>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
// Props:
//   blocks   — initial block data from parent ({ data, total })
//   loading  — parent loading state
//   roomId   — room ID for server-side refetch on filter change
//   knownCoins — optional array of coin strings (e.g. from rewardsBoards) so
//                filter chips show all coins even when excluded from the current page
function BlockMiningHistory({ blocks, loading, roomId, knownCoins }) {
  const t = useDashboardTheme();

  // ── Local data: starts from parent-provided blocks, replaced by self-fetch on filter change
  const [localBlocks, setLocalBlocks] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const activeBlocks = localBlocks || blocks;
  const blockData = activeBlocks?.data || [];

  // ── Coin filter state (server-side) ───────────────────────────────────
  const [hiddenCoins, setHiddenCoins] = useState(() => new Set(DEFAULT_HIDDEN_COINS));

  // Stable serialization of knownCoins prop so useMemo deps don't thrash.
  // knownCoins comes from .map() on every render → always a new array ref,
  // but contents are typically stable. Serialize to string for comparison.
  const knownCoinsKey = useMemo(() => {
    if (!knownCoins || knownCoins.length === 0) return '';
    return [...new Set(knownCoins.filter(Boolean).map((c) => c.toUpperCase()))].sort().join(',');
  }, [knownCoins]);

  // Build the full set of known coins from both data AND knownCoins prop.
  // Uses knownCoinsKey for stability — only recomputes when actual coin set changes.
  const allKnownCoins = useMemo(() => {
    const set = new Set();
    blockData.forEach((b) => { if (b.coin) set.add(b.coin.toUpperCase()); });
    if (knownCoinsKey) knownCoinsKey.split(',').forEach((c) => set.add(c));
    return Array.from(set).sort();
  }, [blockData, knownCoinsKey]);

  // If every known coin would be hidden, treat as "show all" so we never
  // start with an empty table (e.g. KAS-only session with KAS default-hidden).
  const effectiveHidden = useMemo(() => {
    if (allKnownCoins.length > 0 && allKnownCoins.every((c) => hiddenCoins.has(c))) return new Set();
    return hiddenCoins;
  }, [allKnownCoins, hiddenCoins]);

  // Stable string key for effectiveHidden so useEffect deps don't thrash
  // on new Set references with identical contents.
  const effectiveHiddenKey = useMemo(
    () => [...effectiveHidden].sort().join(','),
    [effectiveHidden],
  );

  // ── Server-side refetch when filter changes ───────────────────────────
  const fetchBlocks = useCallback(async (excludeCoins) => {
    if (!roomId) return;
    setLocalLoading(true);
    try {
      const result = await getRoomBlocks(roomId, 50, 0, excludeCoins.length > 0 ? excludeCoins : undefined);
      setLocalBlocks(result);
    } catch (err) {
      console.error('Error fetching filtered blocks:', err);
    }
    setLocalLoading(false);
  }, [roomId]);

  // Trigger refetch when effective exclusion set changes.
  // Uses effectiveHiddenKey (string) as dep to avoid re-firing on
  // identical Set contents with different object references.
  const initialRender = useRef(true);
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      // On initial render, if default hidden coins apply AND we have a roomId,
      // do an immediate fetch so we get correct server-filtered data.
      if (effectiveHiddenKey && roomId) {
        fetchBlocks(effectiveHiddenKey.split(','));
      }
      return;
    }
    if (!effectiveHiddenKey) {
      // No filter — revert to parent-provided data
      setLocalBlocks(null);
    } else {
      fetchBlocks(effectiveHiddenKey.split(','));
    }
  }, [effectiveHiddenKey, fetchBlocks, roomId]);

  const toggleCoin = (coin) => {
    setHiddenCoins((prev) => {
      const next = new Set(prev);
      if (next.has(coin)) next.delete(coin);
      else next.add(coin);
      return next;
    });
  };

  const totalCount = activeBlocks?.total ?? 0;
  const isLoading = loading || localLoading;

  return (
    <Card sx={cardSx}>
      <MDBox sx={panelHeaderSx(t)}>
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
          Block Mining History
        </MDTypography>
        <MDBox sx={countBadgeSx(t)}>
          <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: t.tableHeaderColor, letterSpacing: '0.04em' }}>
            {totalCount} block{totalCount !== 1 ? 's' : ''}
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* Coin filter badges */}
      {allKnownCoins.length > 1 && (
        <MDBox sx={{ px: { xs: 2, sm: 3 }, pt: 1.25, pb: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75, borderBottom: `1px solid ${t.divider}` }}>
          {allKnownCoins.map((coin) => {
            const active = !effectiveHidden.has(coin);
            const c = coinColor(coin);
            return (
              <Chip
                key={coin}
                label={coin}
                size='small'
                onClick={() => toggleCoin(coin)}
                sx={{
                  fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.03em', height: 26, cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  ...(active
                    ? { background: c.bg, color: c.text, '&:hover': { opacity: 0.85 } }
                    : {
                        background: t.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                        color: t.isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                        textDecoration: 'line-through',
                        '&:hover': { background: t.isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)' },
                      }),
                }}
              />
            );
          })}
        </MDBox>
      )}

      {isLoading && blockData.length === 0 ? (
        <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
          <Skeleton variant='rectangular' height={200} sx={{ borderRadius: '8px' }} />
        </MDBox>
      ) : blockData.length === 0 ? (
        <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={4} gap={1}>
          <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>No blocks mined yet</MDTypography>
        </MDBox>
      ) : (
        <TableContainer sx={{ ...scrollbarSx(t), maxHeight: 360 }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow sx={tableHeadRowSx(t)}>
                <TableCell>Time</TableCell>
                <TableCell>Coin</TableCell>
                <TableCell align='right'>BTC</TableCell>
                <TableCell align='right'>USD</TableCell>
                <TableCell>Block Hash</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blockData.map((block, index) => (
                <BlockRow key={block.id} block={block} index={index} t={t} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}

export default BlockMiningHistory;
